import { Field, Form, Formik } from 'formik';
import Button from '../../../../components/buttons/Button';
import { useTranslation } from 'react-i18next';
import FormGalleryUpload from '../../../../components/formInputs/FormGalleryUpload';
import FieldWrapper from '../../../../components/formInputs/FieldWrapper';
import SwitchActivation from '../../../../components/layout/SwitchActivation';
import { customStyles } from '../../../../utils/SelectStyles';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import ExpandAccordion from '../../../products/components/productForm/ExpandAccordion';
import useModelSelectOptions from '../../../../hooks/useModelSelectOptions';
import useFormsIntegrationHelpers from '../../../../hooks/useFormsIntegrationHelpers';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { Fragment, useContext, useMemo, useState } from 'react';
import { generalDelete } from '../../../../API/api';
import { toast } from 'react-toastify';
import { authContext } from '../../../../store/context/authContext';
import { useQueryClient } from '@tanstack/react-query';
import { getMedia, orderData } from '../../../../utils/HelperFunctions';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import FormUpload from '../../../../components/formInputs/FormUpload';

const apisData = {
  exterior: {
    queryKey: 'exterior-color',
    key: 'exterior_color',
    api: (modelId: string | undefined) => `/admin/models/${modelId}/exterior_colors`,
    modelRoute: '/admin/exterior-colors?per_page=2000'
  },
  interior: {
    queryKey: 'interior-color',
    key: 'interior_color',
    api: (modelId: string | undefined) => `/admin/models/${modelId}/interior_colors`,
    modelRoute: '/admin/interior-colors?per_page=2000'
  },
  exterior_variant: {
    queryKey: 'exterior-color',
    key: 'exterior_color',
    api: (variantId: string | undefined) => `/admin/variants/${variantId}/exterior_colors`,
    modelRoute: '/admin/exterior-colors?per_page=2000'
  },
  interior_variant: {
    queryKey: 'interior-color',
    key: 'interior_color',
    api: (variantId: string | undefined) => `/admin/variants/${variantId}/interior_colors`,
    modelRoute: '/admin/interior-colors?per_page=2000'
  }
};

export default function VehicleModelsEditColor({
  data,
  interior,
  add,
  expanded,
  setExpanded,
  index,
  variant
}: {
  data?: any;
  interior?: boolean;
  add?: boolean;
  expanded: number;
  setExpanded: React.Dispatch<React.SetStateAction<number>>;
  index: number;
  variant?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { modelId, variantId } = useParams();
  const { catchError } = useContext(authContext);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const queryClient = useQueryClient();

  const id = variant ? variantId : modelId;

  const routesData = apisData[`${interior ? 'interior' : 'exterior'}${variant ? '_variant' : ''}`];

  const colorLabelKey = useMemo(() => ['code', `display_name.${i18n.language}`], [i18n.language]);
  const { modelOptions: colorsOptions, apiDataLoading: colorsLoading } = useModelSelectOptions({
    labelKey: colorLabelKey,
    valueKey: 'id',
    modelRoute: routesData.modelRoute,
    queryKey: [routesData.queryKey]
  });

  const { handleSubmit, submitLoading } = useFormsIntegrationHelpers({
    id: !add ? '1' : undefined,
    editApi: routesData.api(id),
    addApi: routesData.api(id),
    itemName: t('color'),
    queryKey: [variant ? 'variants' : 'models']
  });

  const validationSchema = Yup.object({
    images: Yup.array().required(t('required'))?.min(1, t('at_least_one_image')),
    color_id: Yup.string().required(t('required'))
  });

  function deleteColor() {
    setDeleteLoading(true);
    generalDelete(`${routesData.api(variant ? variantId : modelId)}/${data?.[routesData?.key]?.id}`)
      .then(() => {
        toast.success(`${t('color')} ${t('deleted')} ${t('successfully')}`);
        queryClient.invalidateQueries({ queryKey: variant ? ['variants'] : ['models'] });
      })
      .catch((error) => {
        catchError(error, setDeleteLoading);
      })
      .finally(() => {
        setDeleteLoading(false);
      });
  }

  return (
    <Formik
      validateOnMount
      enableReinitialize
      validationSchema={validationSchema}
      initialValues={
        add
          ? { images: [], main_image: '', main_image_with_bg: '', color_id: '', featured: 0 }
          : ({
              images: orderData(data?.media?.filter((ele: any) => ele.collection_name === 'images') || [])?.map((ele: any) => ele.url),
              color_id: Number(data?.[routesData?.key]?.id),
              main_image: getMedia(data?.media, 'main_image')?.url,
              main_image_with_bg: getMedia(data?.media, 'main_image_with_bg')?.url,
              featured: data?.featured
            } as {
              images: (
                | {
                    id: number;
                    url: string;
                  }
                | string
                | File
              )[];
              color_id: number;
              main_image: string;
              main_image_with_bg: string;
              featured: 0 | 1;
            })
      }
      // validationSchema={validationSchema}
      onSubmit={(values, { setErrors, resetForm }) => {
        const newValues: any = { ...values };
        newValues.old_color_id = data?.[routesData?.key]?.id;
        newValues.images = values?.images?.map((ele: any, index: number) => ({
          file: ele,
          order: index + 1
        }));
        handleSubmit(newValues, setErrors, add ? resetForm : undefined);
      }}
    >
      {(formik) => {
        return (
          <Form className="model_gallery_color_form">
            <Accordion
              expanded={expanded === index}
              onChange={(e) => {
                e.stopPropagation();
                setExpanded(expanded === index ? -1 : index);
              }}
            >
              <AccordionSummary
                sx={{
                  '&.Mui-focusVisible': {
                    backgroundColor: '#000'
                  }
                }}
                aria-controls="panel1a-content"
                id="panel1a-header"
                className={`header-container ${expanded !== index && 'expand'}`}
              >
                <p>
                  <ExpandAccordion expand={expanded === index} />{' '}
                  {add
                    ? t('new_color')
                    : typeof data?.[routesData?.key]?.display_name === 'object'
                      ? data?.[routesData?.key]?.display_name?.[i18n.language]
                      : data?.[routesData?.key]?.display_name}
                </p>
              </AccordionSummary>
              <AccordionDetails>
                <div className="inputs_group">
                  <FormGalleryUpload label={t('gallery')} errors={formik?.errors?.images as string} touched={formik?.touched?.images as boolean} formik={formik} name={`images`} />
                </div>
                {!interior && (
                  <div className="inputs_group">
                    <FieldWrapper noPadding>
                      <FormUpload
                        label={t('main_image')}
                        errors={formik?.errors?.main_image as string}
                        touched={formik?.touched?.main_image as boolean}
                        formik={formik}
                        name={`main_image`}
                      />
                    </FieldWrapper>
                    <FieldWrapper noPadding>
                      <FormUpload
                        label={t('main_image_with_bg')}
                        errors={formik?.errors?.main_image_with_bg as string}
                        touched={formik?.touched?.main_image_with_bg as boolean}
                        formik={formik}
                        name={`main_image_with_bg`}
                      />
                    </FieldWrapper>
                  </div>
                )}
                <div className="inputs_group">
                  {colorsLoading ? (
                    <Fragment>
                      <div style={{ flex: '1 1' }}>
                        <SkeletonTheme baseColor="#d4d4d4" highlightColor="#e9e9e9">
                          <div className=" skeleton_w-100">
                            <div className="skeleton_flex">
                              <div className="skeleton_flex">
                                <div className="skeleton_w-100">
                                  <Skeleton className="skeleton_input-title" />
                                  <Skeleton className="skeleton_input skeleton_w-100" />
                                </div>
                              </div>
                            </div>
                          </div>
                        </SkeletonTheme>
                      </div>
                      {add && <FieldWrapper />}
                    </Fragment>
                  ) : (
                    <FieldWrapper
                      noPadding
                      title={t('color')}
                      inputPlaceholder={t('color')}
                      inputName={`color_id`}
                      options={colorsOptions}
                      value={(colorsOptions as any)?.find((ele: any) => ele.value === formik.values.color_id) || null}
                      selectStyle={customStyles}
                      select
                      search
                    />
                  )}
                  {!add ? (
                    <div className="switch_container">
                      <SwitchActivation onChange={(value: any) => formik.setFieldValue('featured', value?.target?.checked ? 1 : 0)} active={formik.values.featured} />
                      <p>{t('featured')}</p>
                    </div>
                  ) : (
                    !colorsLoading && <FieldWrapper />
                  )}
                </div>
                <div className="model_gallery_color_buttons">
                  <div className="form_button reverse">
                    <Button
                      type="button"
                      loading={submitLoading}
                      onClick={() => {
                        formik.submitForm();
                      }}
                    >
                      <span className="bold">{t(add ? 'submit' : 'update')}</span>
                    </Button>
                  </div>
                  {!add && (
                    <div className="form_button reverse">
                      <Button
                        type="button"
                        customClass="red"
                        loading={deleteLoading}
                        onClick={() => {
                          deleteColor();
                        }}
                      >
                        <span className="bold">{t('delete')}</span>
                      </Button>
                    </div>
                  )}
                </div>
              </AccordionDetails>
            </Accordion>
          </Form>
        );
      }}
    </Formik>
  );
}
